import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import type { PageProps } from 'gatsby';
import React from 'react';
import Collection from '../../components/Collection';
import ConservationBanner from '../../components/ConservationBanner';
import Firmware from '../../components/Firmware';
import ImageTextSlide from '../../components/ImageTextSlide';
import Layout from '../../components/Layout';
import Login from '../../components/Login';
import PlatformResources, {
  PlatformBoxProps,
} from '../../components/PlatformResources';
import SectionHeader from '../../components/SectionHeader';
import { SeoBasic } from '../../components/Seo';
import {
  ImgDoomVersio,
  ImgLibDaisy,
  ImgOpenSource,
  ImgPanelsVersio,
  ImgSwapVersio,
} from '../../components/StaticImages';
import TriImageBanner from '../../components/TriImageBanner';

const resources: PlatformBoxProps[] = [
  {
    title: 'Manuals',
    desc: 'Go in-depth with the different Versio firmwares.',
    href: 'https://manuals.noiseengineering.us/',
    buttonText: 'Read all about it',
  },
  {
    title: 'LibDaisy',
    desc: 'Want to develop your own firmware for your Versio? Check out LibDaisy! ',
    href: 'https://github.com/electro-smith/libDaisy',
    buttonText: 'Take me to LibDaisy',
  },
  {
    title: 'Printable Panels',
    desc: 'Want to try out an alternative firmware? Print out a panel overlay or use the DXF to create your own!',
    href: 'https://cdn.shopify.com/s/files/1/0552/0923/0485/files/versio_printable.zip?v=1733443477',
    buttonText: 'Download',
  },
  {
    title: 'Order Panel Overlays',
    desc: 'Want to hotswap a panel? Look no further: panel overlays for all your Versio needs, in black and in white.',
    href: '/products/versio-panel-overlays',
    buttonText: 'Order Now',
  },
  {
    title: 'Video Tutorials',
    desc: 'Let the batverb into your earholes.',
    href: 'https://youtube.com/playlist?list=PL7ojIvQU8WHv02rzhjv42UTmHmjWLkuVu',
    buttonText: 'Explore Videos',
  },
];
const bannerImages: BlockInfo[] = [
  {
    image: ImgSwapVersio,
    text: 'Library of swappable, free firmwares',
  },
  {
    image: ImgDoomVersio,
    text: 'Unique features like DUCK, DOOM, and FSU',
  },
  {
    image: ImgOpenSource,
    text: 'Open source: Create your own firmware',
  },
];

export default function PageWoV(props: React.FC<PageProps>) {
  return (
    <Layout>
      <ImageTextSlide
        image={ImgPanelsVersio}
        alt='a sphere with all the Versio panels on it and a banner that says World of Versio'
        title='Welcome to the World of Versio'
        body='Unlock the full potential of your Versio with alternate firmwares and DIY resources. '
        body2='Versio is a shapeshifting effects platform. Turn your Versio into a clocked delay, ADSR-controlled VCA, or something completely different! Our firmware is always free for Versio owners, so change your Versio module whenever inspiration strikes! Forget menu diving. Say hello to a whole new building block for modular synthesis.'
        flip={false}
        textColor='info.main'
      />
      <TriImageBanner images={bannerImages}></TriImageBanner>
      <SectionHeader
        title='modules'
        buttonText='View All'
        href='/collections/versio'
      ></SectionHeader>
      <Collection count={3} includes={['versio']} />
      <Firmware
        showFull={false}
        title='Firmware swapping made easy'
        heading='Choose your next adventure'
        text='Swap to a new firmware any time, free of charge, easy as can be with the Versio platform'
        text2='Click Open Wizard for step-by-step instructions to swap to a new firmware. '
      />
      <SectionHeader title='resources'></SectionHeader>
      <PlatformResources resources={resources}></PlatformResources>
      <Box bgcolor='info.main' color='error.main' mb={0} mt={2}>
        <ImageTextSlide
          image={ImgLibDaisy}
          alt='Versio is an open-source platform. Check out the resources and join the community on libDaisy for more information.'
          title="It's an Open-Source Platform"
          body='Want to write your own firmware for Versio? You can. Check out the resources and join the community on libDaisy for more information.'
          href='https://github.com/electro-smith/libDaisy'
          linkname='Take me to libDaisy'
          flip={false}
          fit='scale-down'
          textColor='white'
          buttonColor='error'
          class='classBlackBg'
        />
      </Box>
      <Divider role='presentation' color='black' />
      <ConservationBanner conservation='bat' />
    </Layout>
  );
}

export function Head() {
  return (
    <>
      {' '}
      <SeoBasic
        title='The World of Versio'
        path='/pages/world-of-versio/'
        description='Welcome to the World of Versio, home of resources for our 10HP swappable FX platform'
        image='/logo.png'
        imageAlt='Noise Engineering Logo'
      />
    </>
  );
}
